/**
 * Real backend: Supabase RPCs (SPEC §2, exact names/params) + edge functions (SPEC §3).
 * Reads that the contract has no RPC for go through table selects that rely on member/staff RLS
 * (policies in 0110). Shapes are adapted below from what the SQL bodies return; the contract is
 * checked statically by `npm run contract` (scripts/contract-check.mjs).
 */
import { createClient, type SupabaseClient, type PostgrestError } from '@supabase/supabase-js';
import { env } from '@/lib/env';
import { toJpeg } from '@/lib/image';
import { parseCsv } from '@/lib/csv';
import { ApiError, type Api, type Session } from './api';
import type * as T from './types';

let _sb: SupabaseClient | null = null;
function sb(): SupabaseClient {
  if (!_sb) {
    _sb = createClient(env.supabaseUrl, env.supabaseAnonKey, {
      auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
    });
  }
  return _sb;
}

function toApiError(err: PostgrestError | Error | { message?: string; code?: string; details?: string } | null): ApiError {
  if (!err) return new ApiError('Unknown error');
  const e = err as { message?: string; code?: string; details?: string; hint?: string };
  // 22023 = invalid_parameter_value: save_listing returns the field list in details/hint as JSON
  let fields: T.FieldError[] = [];
  const raw = e.details || e.hint;
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) {
        fields = parsed
          .map((f) => (typeof f === 'string' ? { field: f, message: 'Invalid value' } : (f as T.FieldError)))
          .filter((f) => f && typeof f.field === 'string');
      } else if (parsed && typeof parsed === 'object' && Array.isArray((parsed as { fields?: unknown }).fields)) {
        fields = (parsed as { fields: T.FieldError[] }).fields;
      }
    } catch {
      /* details was prose */
    }
  }
  return new ApiError(e.message || 'Request failed', e.code || 'error', fields);
}

async function rpc<R>(fn: string, args: Record<string, unknown> = {}): Promise<R> {
  const { data, error } = await sb().rpc(fn, args);
  if (error) throw toApiError(error);
  return data as R;
}

async function rows<R>(q: PromiseLike<{ data: unknown; error: PostgrestError | null }>): Promise<R[]> {
  const { data, error } = await q;
  if (error) throw toApiError(error);
  return (data ?? []) as R[];
}

async function edge<R>(name: string, body: Record<string, unknown>): Promise<R> {
  const { data, error } = await sb().functions.invoke(name, { body });
  if (error) {
    let message = error.message;
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const j = (await ctx.json()) as { error?: string; message?: string };
        message = j.error || j.message || message;
      } catch {
        /* not json */
      }
    }
    throw new ApiError(message, 'edge_error');
  }
  return data as R;
}

// ── Shape adapters ──────────────────────────────────────────────────────────────
// Every adapter below turns what the SQL in backend/supabase/migrations/0111 ACTUALLY returns into
// the portal's types. scripts/contract-check.mjs pins the keys read here against the SQL bodies.
/* eslint-disable @typescript-eslint/no-explicit-any */
type Json = any;

/** Buckets (0110 §Storage). listing-photos is 0037's public bucket (<uid>/…). */
const BUCKET = {
  photos: 'listing-photos', // public; <uid>/…
  docs: 'listing-docs', // PRIVATE; <account_id>/<listing_id>/… (path prefix checked by RLS)
  leadFiles: 'lead-attachments', // PRIVATE; <lead_id>/… (both parties of the lead)
  creatives: 'mkt-creatives', // public; <account_id>/… (owner/admin write)
} as const;

const num = (v: unknown, d = 0): number => (v == null || v === '' || Number.isNaN(Number(v)) ? d : Number(v));
const arr = <X>(v: unknown): X[] => (Array.isArray(v) ? (v as X[]) : []);

/** hh_mkt_my_accounts: `to_jsonb(account) || {role, member_status}`. */
function normalizeMyAccount(r: Json): T.MyAccount {
  if (r && typeof r.account === 'object' && r.account) return { ...(r.account as T.Account), role: r.role as T.MemberRole };
  // Stripe ids / card fingerprint are deliberately not returned to every role (billing summary has the card).
  return {
    ...(r as T.Account), stripe_customer_id: r.stripe_customer_id ?? null, default_payment_method_id: r.default_payment_method_id ?? null,
    storefront: (r?.storefront ?? {}) as T.Storefront, role: r.role as T.MemberRole,
  };
}

/** hh_mkt_my_listings: hh_mkt_card ∪ every editable column ∪ {stats, documents, scheduled_price_changes}. */
function normalizeListing(o: Json): T.MyListing {
  return {
    ...(o as T.MyListing),
    description: o.description ?? null,
    condition: o.condition ?? null,
    images: arr<string>(o.images),
    attributes: (o.attributes ?? {}) as T.Attributes,
    custom_attributes: arr<Json>(o.custom_attributes).map((c) => ({
      label: String(c.label ?? ''),
      value: c.value == null ? '' : typeof c.value === 'string' ? c.value : JSON.stringify(c.value),
      mapped_key: c.mapped_key ?? null,
      confidence: c.confidence == null ? null : num(c.confidence),
    })),
    review_reasons: arr<T.ReviewReason>(o.review_reasons),
    currency: o.currency ?? 'USD',
    stats: {
      views: num(o.stats?.views), saves: num(o.stats?.saves), leads: num(o.stats?.leads), spend_cents: num(o.stats?.spend_cents),
    },
    documents: arr<T.ListingDocument>(o.documents),
    scheduled_price_changes: arr<T.ScheduledPriceChange>(o.scheduled_price_changes),
  };
}

/** hh_mkt_submit_listing `missing` codes (hh_mkt_missing_fields + submit checks) → editor field keys. */
const MISSING_CODE: Record<string, string> = {
  make: 'make_id', model: 'model_id', photo: 'images', price: 'price_cents', location: 'location_state',
};

/** hh_mkt_inbox row: `to_jsonb(lead) - reply_token` ∪ {listing:{id,title,slug,thumb,state}, buyer_display,
 *  last_message:{body,sender_side,created_at,flags}, unread}. */
function toInboxRow(o: Json): T.InboxRow {
  const lm = o.last_message ?? null;
  return {
    ...(o as T.Lead),
    buyer_name: o.buyer_name ?? o.buyer_display ?? null,
    reply_token: '',
    listing_title: o.listing?.title ?? null,
    listing_thumb: o.listing?.thumb ?? null,
    last_message: lm?.body ?? null,
    last_message_at: lm?.created_at ?? null,
    last_sender_side: lm?.sender_side ?? null,
    unread: num(o.unread),
    flags: arr<string>(lm?.flags),
  };
}

/** hh_mkt_campaign_stats: {from,to,campaigns:[{id,name,kind,status,listing_id,totals,days[]}]}. */
function toCampaignStats(r: Json, from: string, to: string): T.CampaignStats {
  const point = (x: Json) => ({
    impressions: num(x?.impressions), clicks: num(x?.clicks), leads: num(x?.leads), cost_cents: num(x?.cost_cents),
  });
  const cs = arr<Json>(r?.campaigns);
  return {
    from, to,
    campaigns: cs.map((c) => ({ campaign_id: c.id, name: c.name, kind: c.kind, day: '', ...point(c.totals) })),
    days: cs.flatMap((c) => arr<Json>(c.days).map((d) => ({ campaign_id: c.id, day: String(d.day).slice(0, 10), ...point(d) }))),
  };
}

/** hh_mkt_set_campaign_status problem codes → portal launch-error codes + copy. */
const CAMPAIGN_PROBLEM: Record<string, { code: string; message: string }> = {
  card: { code: 'card_required', message: 'Add a card on file before launching.' },
  account_active: { code: 'account_inactive', message: 'This account is suspended.' },
  daily_budget: { code: 'daily_budget', message: 'Set a daily budget of at least the policy minimum.' },
  ends_at: { code: 'ends_at', message: 'The end date has already passed.' },
  approved_creative: {
    code: 'no_creatives',
    message: 'Nothing approved to show yet: promoted listings must be live and fully approved (not limited), and brand cards must pass review.',
  },
  audience_too_small: { code: 'audience_too_small', message: 'The audience is below the minimum for person-level targeting. Broaden it.' },
  tier_campaign_cap: { code: 'tier_campaign_cap', message: 'New accounts can run one promotion at a time. End or pause the other one first.' },
};

const REDEEM_REASON: Record<string, string> = {
  invalid_code: 'That code isn’t valid, or the offer has ended.',
  card_required: 'Add a card on file before redeeming this code.',
  fully_redeemed: 'This code has been fully redeemed.',
  already_redeemed: 'This account has already redeemed this code.',
};

/** mkt-import mapping targets: core field names, 'attr:<key>' (or 'attr:<key>[i].<field>'), 'custom', 'ignore'.
 *  The portal shows attribute keys bare and "don't import" as null. */
const EDGE_CORE = new Set(['external_id', 'title', 'description', 'kind', 'make', 'model', 'year', 'serial_number', 'registration',
  'price', 'price_on_request', 'condition', 'location', 'location_ident', 'location_state', 'location_country', 'images']);
function fromEdgeTarget(t: string | null | undefined): string | null {
  if (!t || t === 'ignore') return null;
  const m = /^attr:([a-z0-9_]+)$/.exec(t);
  return m ? m[1] : t;
}
function toEdgeTarget(t: string | null | undefined): string {
  if (!t) return 'ignore';
  if (t === 'custom' || t === 'ignore' || t.startsWith('attr:') || EDGE_CORE.has(t)) return t;
  return `attr:${t}`;
}

const DEFAULT_SETTINGS: T.AccountSettings = {
  notifications: { new_lead_email: true, new_message_email: true, budget_low_email: true, listing_status_email: true },
  lead_routing: { mode: 'unassigned', fixed_user_id: null, sales_user_ids: [], auto_reply: null },
};

function uid(): string {
  return crypto.randomUUID();
}

function ext(name: string): string {
  const m = /\.([a-z0-9]+)$/i.exec(name);
  return m ? m[1].toLowerCase() : 'bin';
}

async function currentUserId(): Promise<string> {
  const { data } = await sb().auth.getUser();
  if (!data.user) throw new ApiError('Not signed in', 'auth');
  return data.user.id;
}

async function upload(bucket: string, path: string, file: Blob, contentType?: string, cacheControl?: string): Promise<void> {
  const { error } = await sb().storage.from(bucket).upload(path, file, { contentType: contentType || undefined, cacheControl, upsert: false });
  if (error) throw new ApiError(error.message, 'upload');
}

function publicUrl(bucket: string, path: string): string {
  return sb().storage.from(bucket).getPublicUrl(path).data.publicUrl;
}

/** Private buckets: short-lived signed URLs (the storage policies decide who may mint them). */
async function signedUrls(bucket: string, paths: string[], seconds = 3600): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  if (!paths.length) return out;
  const { data } = await sb().storage.from(bucket).createSignedUrls(paths, seconds);
  for (const d of data ?? []) if (d.path && d.signedUrl) out.set(d.path, d.signedUrl);
  return out;
}

async function importRunById(runId: string | null | undefined, fallback: Partial<T.ImportRun>): Promise<T.ImportRun> {
  if (runId) {
    const { data } = await sb().from('mkt_import_runs').select('*').eq('id', runId).maybeSingle();
    if (data) return data as T.ImportRun;
  }
  return {
    id: runId ?? uid(), account_id: '', feed_id: null, source: 'csv_upload', started_at: new Date().toISOString(), finished_at: null,
    created: 0, updated: 0, unchanged: 0, removed: 0, failed: 0, errors: [], ...fallback,
  };
}

export function createRealApi(): Api {
  return {
    mode: 'real',

    // ── Auth ──
    async getSession() {
      const { data } = await sb().auth.getSession();
      const u = data.session?.user;
      return u ? { user_id: u.id, email: u.email ?? '' } : null;
    },
    onAuthChange(cb) {
      const { data } = sb().auth.onAuthStateChange((_evt, session) => {
        const u = session?.user;
        cb(u ? ({ user_id: u.id, email: u.email ?? '' } satisfies Session) : null);
      });
      return () => data.subscription.unsubscribe();
    },
    async signInWithPassword(email, password) {
      const { error } = await sb().auth.signInWithPassword({ email, password });
      if (error) throw new ApiError(error.message, 'auth');
    },
    async signUp(email, password, displayName) {
      const { data, error } = await sb().auth.signUp({
        email, password,
        options: { data: { display_name: displayName }, emailRedirectTo: `${window.location.origin}/onboarding` },
      });
      if (error) throw new ApiError(error.message, 'auth');
      return { needsConfirmation: !data.session };
    },
    async sendMagicLink(email) {
      const { error } = await sb().auth.signInWithOtp({ email, options: { emailRedirectTo: window.location.origin } });
      if (error) throw new ApiError(error.message, 'auth');
    },
    async signOut() {
      await sb().auth.signOut();
    },
    async me() {
      const { data } = await sb().auth.getUser();
      if (!data.user) throw new ApiError('Not signed in', 'auth');
      const meta = (data.user.user_metadata ?? {}) as { display_name?: string; full_name?: string };
      const isStaff = await rpc<boolean>('hh_mkt_is_staff').catch(() => false);
      return {
        id: data.user.id,
        email: data.user.email ?? '',
        display_name: meta.display_name || meta.full_name || (data.user.email ?? '').split('@')[0],
        is_staff: Boolean(isStaff),
      };
    },

    // ── Accounts ──
    async myAccounts() {
      const r = await rpc<Json[]>('hh_mkt_my_accounts');
      return (r ?? []).map(normalizeMyAccount);
    },
    myIndividualAccount: () => rpc<T.Account>('hh_mkt_my_account'),
    async createDealerAccount(input) {
      let companyId = input.company_id ?? null;
      if (!companyId) {
        // 0033 hh_create_company (security invoker): inserts the company + the caller's 'owner' row,
        // which hh_mkt_create_dealer_account maps to account owner.
        companyId = await rpc<string>('hh_create_company', { p_name: input.company_name, p_website: input.website || null });
      }
      return rpc<string>('hh_mkt_create_dealer_account', {
        p_company_id: companyId, p_display_name: input.display_name, p_slug: input.slug,
      });
    },
    updateStorefront: (accountId, storefront, displayName) =>
      rpc<void>('hh_mkt_update_storefront', { p_account: accountId, p_storefront: storefront, p_display_name: displayName }),
    async members(accountId) {
      // hh_mkt_members: member rows + display_name (pilot_handles) + email (auth.users / invited_email).
      const r = await rpc<Json[]>('hh_mkt_members', { p_account: accountId });
      return (r ?? []).map((m) => ({ ...(m as T.Member), display_name: m.display_name ?? null, email: m.email ?? null }));
    },
    inviteMember: (accountId, email, role) =>
      rpc<string>('hh_mkt_invite_member', { p_account: accountId, p_email: email, p_role: role }),
    setMemberRole: (accountId, userId, role) =>
      rpc<void>('hh_mkt_set_member_role', { p_account: accountId, p_user: userId, p_role: role }),
    removeMember: (accountId, userId) => rpc<void>('hh_mkt_remove_member', { p_account: accountId, p_user: userId }),
    setInviteRole: (accountId, email, role) =>
      rpc<void>('hh_mkt_set_invite_role', { p_account: accountId, p_email: email, p_role: role }),
    cancelInvite: (accountId, email) => rpc<void>('hh_mkt_cancel_invite', { p_account: accountId, p_email: email }),
    setAutoRecharge: (accountId, enabled, threshold, amount) =>
      rpc<void>('hh_mkt_set_auto_recharge', {
        p_account: accountId, p_enabled: enabled, p_threshold_cents: threshold, p_amount_cents: amount,
      }),
    async getSettings(accountId) {
      const r = await rpc<Json>('hh_mkt_account_settings', { p_account: accountId });
      return {
        notifications: { ...DEFAULT_SETTINGS.notifications, ...(r?.notifications ?? {}) },
        lead_routing: {
          ...DEFAULT_SETTINGS.lead_routing, ...(r?.lead_routing ?? {}),
          sales_user_ids: arr<string>(r?.lead_routing?.sales_user_ids),
        },
      };
    },
    saveSettings: (accountId, settings) =>
      rpc<void>('hh_mkt_update_account_settings', { p_account: accountId, p_settings: settings }),

    // ── Taxonomy ──
    attributeDefs: (kind) => rpc<T.AttributeDef[]>('hh_mkt_attribute_defs', { p_kind: kind }),
    makes: (cls) => rpc<T.Make[]>('hh_mkt_makes', { p_class: cls ?? null }),
    models: (makeId) => rpc<T.Model[]>('hh_mkt_models', { p_make_id: makeId }),

    // ── Listings ──
    quote: (accountId, kind) => rpc<T.Quote>('hh_mkt_quote', { p_account: accountId, p_kind: kind }),
    async myListings(accountId) {
      const r = await rpc<Json[]>('hh_mkt_my_listings', { p_account: accountId });
      return (r ?? []).map(normalizeListing);
    },
    saveListing: (input) => rpc<string>('hh_mkt_save_listing', { p_listing: input }),
    async submitListing(id) {
      const r = await rpc<T.SubmitResult>('hh_mkt_submit_listing', { p_id: id });
      return { ...r, missing: arr<string>(r.missing).map((m) => MISSING_CODE[m] ?? m) };
    },
    setListingState: (id, state) => rpc<void>('hh_mkt_set_listing_state', { p_id: id, p_state: state }),
    async comps(listingId) {
      // hh_mkt_comps items are listing cards (+ sold_at); days on market from published_at.
      const r = await rpc<Json>('hh_mkt_comps', { p_listing: listingId });
      return {
        listing_id: r.listing_id,
        count: num(r.count),
        median_price_cents: r.median_price_cents ?? null,
        median_days_on_market: r.median_days_on_market ?? null,
        items: arr<Json>(r.items).map((i) => ({
          id: i.id, title: i.title, year: i.year ?? null, price_cents: i.price_cents ?? null, state: i.state,
          hours: i.hours == null ? null : num(i.hours), sold_at: i.sold_at ?? null,
          days_on_market: i.published_at
            ? Math.max(0, Math.round(((i.sold_at ? Date.parse(i.sold_at) : Date.now()) - Date.parse(i.published_at)) / 86_400_000))
            : null,
        })),
      };
    },
    async uploadListingPhoto(original) {
      // 0037 listing-photos (public): first folder must be the uploader's uid.
      const userId = await currentUserId();
      const file = await toJpeg(original);
      const path = `${userId}/${uid()}.jpg`;
      await upload(BUCKET.photos, path, file, 'image/jpeg', '31536000');
      return { path, url: publicUrl(BUCKET.photos, path) };
    },
    async uploadListingDocument(listingId, file, kind, isPublic) {
      // PRIVATE listing-docs/<account_id>/<listing_id>/…: storage policy mkt_listing_docs_member and
      // mkt_listing_documents' write policy both require the listing's own account as first folder.
      const { data: l, error: le } = await sb().from('listings').select('account_id').eq('id', listingId).single();
      if (le || !l?.account_id) throw le ? toApiError(le) : new ApiError('Listing not found', 'not_found');
      const path = `${l.account_id as string}/${listingId}/${uid()}.${ext(file.name)}`;
      await upload(BUCKET.docs, path, file, file.type);
      const { data, error } = await sb()
        .from('mkt_listing_documents')
        .insert({ listing_id: listingId, kind, path, name: file.name, public: isPublic })
        .select('*')
        .single();
      if (error) {
        await sb().storage.from(BUCKET.docs).remove([path]);
        throw toApiError(error);
      }
      return data as T.ListingDocument;
    },
    async deleteListingDocument(docId) {
      const { data } = await sb().from('mkt_listing_documents').select('path').eq('id', docId).maybeSingle();
      const { error } = await sb().from('mkt_listing_documents').delete().eq('id', docId);
      if (error) throw toApiError(error);
      if (data?.path) await sb().storage.from(BUCKET.docs).remove([data.path as string]);
    },
    async schedulePriceChange(listingId, priceCents, effectiveAt) {
      const { data, error } = await sb()
        .from('mkt_scheduled_price_changes')
        .insert({ listing_id: listingId, price_cents: priceCents, effective_at: effectiveAt })
        .select('*')
        .single();
      if (error) throw toApiError(error);
      return data as T.ScheduledPriceChange;
    },
    async cancelPriceChange(id) {
      const { error } = await sb().from('mkt_scheduled_price_changes').delete().eq('id', id).is('applied_at', null);
      if (error) throw toApiError(error);
    },

    // ── Import / feeds (mkt-import: {action, account, content, mapping?} / {action:'run_feed', account, feed_id}) ──
    async importPreview(accountId, csv) {
      const r = await edge<Json>('mkt-import', { action: 'preview', account: accountId, content: csv, format: 'csv' });
      const headers = arr<string>(r.headers);
      // The edge's `sample` is already MAPPED; the mapping UI shows raw cells, read locally (same column order).
      const rows = parseCsv(csv).slice(1, 6);
      return {
        headers,
        row_count: num(r.rows),
        sample_rows: rows.map((row) => Object.fromEntries(headers.map((h, i) => [h, (row[i] ?? '').trim()]))),
        suggested_mapping: Object.fromEntries(headers.map((h) => [h, fromEdgeTarget(r.suggested_mapping?.[h])])),
      };
    },
    async importRun(accountId, csv, mapping) {
      const r = await edge<Json>('mkt-import', {
        action: 'run', account: accountId, content: csv, format: 'csv',
        mapping: Object.fromEntries(Object.entries(mapping).map(([h, t]) => [h, toEdgeTarget(t)])),
      });
      return importRunById(r.run_id, {
        account_id: accountId, source: 'csv_upload', created: num(r.created), updated: num(r.updated), unchanged: num(r.unchanged),
        removed: num(r.removed), failed: num(r.failed), errors: arr(r.errors), finished_at: new Date().toISOString(),
      });
    },
    importRuns: (accountId) =>
      rows<T.ImportRun>(sb().from('mkt_import_runs').select('*').eq('account_id', accountId).order('started_at', { ascending: false }).limit(50)),
    feeds: (accountId) =>
      rows<T.Feed>(sb().from('mkt_feeds').select('*').eq('account_id', accountId).order('created_at', { ascending: false })),
    async saveFeed(feed) {
      const { data, error } = await sb().from('mkt_feeds').upsert(feed).select('id').single();
      if (error) throw toApiError(error);
      return (data as { id: string }).id;
    },
    async deleteFeed(id) {
      const { error } = await sb().from('mkt_feeds').update({ active: false }).eq('id', id);
      if (error) throw toApiError(error);
    },
    async runFeedNow(id) {
      const { data: f, error: fe } = await sb().from('mkt_feeds').select('account_id').eq('id', id).single();
      if (fe) throw toApiError(fe);
      const r = await edge<Json>('mkt-import', { action: 'run_feed', account: f.account_id, feed_id: id });
      if (r && typeof r.error === 'string') throw new ApiError(r.error, 'import');
      return importRunById(r.run_id, {
        account_id: f.account_id as string, feed_id: id, source: 'feed', created: num(r.created), updated: num(r.updated),
        unchanged: num(r.unchanged), removed: num(r.removed), failed: num(r.failed), errors: arr(r.errors),
      });
    },

    // ── Leads ──
    async inbox(accountId, filter) {
      // hh_mkt_inbox filters on ONE status; the portal's chips ask for several, and for free text.
      const f: Record<string, unknown> = {};
      if (filter.assigned) f.assigned = filter.assigned;
      if (filter.listing_id) f.listing_id = filter.listing_id;
      if (filter.unread) f.unread = true;
      if (filter.status?.length === 1) f.status = filter.status[0];
      const r = await rpc<Json[]>('hh_mkt_inbox', { p_account: accountId, p_filter: f });
      let out = (r ?? []).map(toInboxRow);
      if (filter.status && filter.status.length > 1) out = out.filter((x) => filter.status!.includes(x.status));
      if (filter.q) {
        const q = filter.q.toLowerCase();
        out = out.filter((x) => [x.buyer_name, x.buyer_email, x.listing_title, x.last_message].some((v) => v?.toLowerCase().includes(q)));
      }
      return out;
    },
    async leadThread(leadId) {
      // hh_mkt_lead_thread strips reply_token; sellers may read it from mkt_leads (seller-only RLS).
      const [t, tok] = await Promise.all([
        rpc<Json>('hh_mkt_lead_thread', { p_lead: leadId }),
        sb().from('mkt_leads').select('reply_token').eq('id', leadId).maybeSingle(),
      ]);
      const msgs = arr<Json>(t.messages);
      const paths = msgs.flatMap((m) => arr<Json>(m.attachments).map((a) => String(a.path ?? ''))).filter(Boolean);
      const urls = await signedUrls(BUCKET.leadFiles, [...new Set(paths)]).catch(() => new Map<string, string>());
      const l = t.listing;
      return {
        lead: { ...(t.lead as T.Lead), reply_token: (tok.data?.reply_token as string | undefined) ?? '' },
        listing: l ? { id: l.id, title: l.title, thumb: l.thumb ?? null, price_cents: l.price_cents ?? null, state: l.state } : null,
        messages: msgs.map((m) => ({
          id: m.id, lead_id: m.lead_id ?? leadId, sender_id: m.sender_id ?? null, sender_side: m.sender_side, body: m.body,
          attachments: arr<T.Attachment>(m.attachments).map((a) => ({ ...a, url: urls.get(a.path) })),
          via: m.via, flags: arr<string>(m.flags), created_at: m.created_at,
          read_by_buyer_at: m.read_by_buyer_at ?? null, read_by_seller_at: m.read_by_seller_at ?? null,
        })),
      };
    },
    sendMessage: (leadId, body, attachments = []) =>
      rpc<string>('hh_mkt_send_message', {
        p_lead: leadId,
        // The SQL requires 1–8000 characters of text; an attachment-only reply gets a caption.
        p_body: body.trim() || (attachments.length ? `Attachment: ${attachments.map((a) => a.name).join(', ')}` : ''),
        // Never persist the signed URL (it expires) — only {path,name,mime,size}.
        p_attachments: attachments.map(({ path, name, mime, size }) => ({ path, name, mime, size })),
      }),
    markRead: (leadId) => rpc<void>('hh_mkt_mark_read', { p_lead: leadId }),
    updateLead: (leadId, patch) => rpc<void>('hh_mkt_update_lead', { p_lead: leadId, p_patch: patch }),
    async uploadLeadAttachment(leadId, file) {
      // PRIVATE lead-attachments/<lead_id>/…: storage policy mkt_lead_attachments_party and
      // hh_mkt_send_message both require the lead id as first folder.
      const path = `${leadId}/${uid()}.${ext(file.name)}`;
      await upload(BUCKET.leadFiles, path, file, file.type);
      const urls = await signedUrls(BUCKET.leadFiles, [path]).catch(() => new Map<string, string>());
      return { path, name: file.name, mime: file.type || 'application/octet-stream', size: file.size, url: urls.get(path) };
    },
    savedReplies: (accountId) =>
      rows<T.SavedReply>(sb().from('mkt_saved_replies').select('*').eq('account_id', accountId).order('title')),
    async saveSavedReply(reply) {
      const { data, error } = await sb().from('mkt_saved_replies').upsert(reply).select('id').single();
      if (error) throw toApiError(error);
      return (data as { id: string }).id;
    },
    async deleteSavedReply(id) {
      const { error } = await sb().from('mkt_saved_replies').delete().eq('id', id);
      if (error) throw toApiError(error);
    },
    subscribeLeads(accountId, onChange) {
      // Both tables are in supabase_realtime (0110); RLS filters each change per subscriber.
      const ch = sb()
        .channel(`mkt-leads-${accountId}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'mkt_leads', filter: `account_id=eq.${accountId}` },
          (p) => onChange({ lead_id: String((p.new as { id?: string })?.id ?? (p.old as { id?: string })?.id ?? ''), kind: 'lead' }))
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'mkt_lead_messages' },
          (p) => onChange({ lead_id: String((p.new as { lead_id?: string }).lead_id ?? ''), kind: 'message' }))
        .subscribe();
      return () => {
        void sb().removeChannel(ch);
      };
    },

    // ── Campaigns ──
    campaigns: (accountId) =>
      rows<T.Campaign>(
        sb().from('mkt_campaigns').select('*, creatives:mkt_creatives(*)').eq('account_id', accountId).order('created_at', { ascending: false }),
      ),
    async campaign(id) {
      const { data, error } = await sb().from('mkt_campaigns').select('*, creatives:mkt_creatives(*)').eq('id', id).single();
      if (error) throw toApiError(error);
      return data as T.Campaign;
    },
    saveCampaign: (input) => rpc<string>('hh_mkt_save_campaign', { p_campaign: input }),
    saveCreative: (input) => rpc<string>('hh_mkt_save_creative', { p_creative: input }),
    deleteCreative: (id) => rpc<void>('hh_mkt_delete_creative', { p_id: id }),
    async setCampaignStatus(id, status) {
      const r = await rpc<Json>('hh_mkt_set_campaign_status', { p_id: id, p_status: status });
      return {
        ok: Boolean(r.ok),
        status: r.status as T.CampaignStatus,
        errors: arr<string>(r.problems).map((p) => CAMPAIGN_PROBLEM[p] ?? { code: p, message: p.replace(/_/g, ' ') }),
      };
    },
    estimateAudience: (targeting) => rpc<T.AudienceEstimate>('hh_mkt_estimate_audience', { p_targeting: targeting }),
    async campaignStats(accountId, from, to) {
      return toCampaignStats(await rpc<Json>('hh_mkt_campaign_stats', { p_account: accountId, p_from: from, p_to: to }), from, to);
    },
    async dailyActivity(accountId, from, to) {
      // Organic views/saves/leads per day (hh_mkt_account_daily) + ad impressions/clicks/spend (campaign stats).
      const [daily, stats] = await Promise.all([
        rpc<Json[]>('hh_mkt_account_daily', { p_account: accountId, p_from: from, p_to: to }),
        rpc<Json>('hh_mkt_campaign_stats', { p_account: accountId, p_from: from, p_to: to })
          .then((r) => toCampaignStats(r, from, to)).catch(() => null),
      ]);
      const out = new Map<string, T.DailyActivity>();
      for (const d of daily ?? []) {
        const day = String(d.day).slice(0, 10);
        out.set(day, { day, views: num(d.views), saves: num(d.saves), leads: num(d.leads), impressions: 0, clicks: 0, spend_cents: 0 });
      }
      for (const s of stats?.days ?? []) {
        const r = out.get(s.day);
        if (r) { r.impressions += s.impressions; r.clicks += s.clicks; r.spend_cents += s.cost_cents; }
      }
      return [...out.values()];
    },
    async currentPolicy() {
      const r = await rpc<T.PricingPolicy | T.PricingPolicy[]>('hh_mkt_current_policy');
      return Array.isArray(r) ? r[0] : r;
    },
    async uploadCreativeImage(accountId, original) {
      const file = await toJpeg(original);
      const path = `${accountId}/creatives/${uid()}.jpg`;
      await upload(BUCKET.creatives, path, file, 'image/jpeg', '31536000');
      return { path, url: publicUrl(BUCKET.creatives, path) };
    },

    // ── Billing ──
    async billingSummary(accountId) {
      // hh_mkt_billing_summary: {account_id, balances:{…}, grants[], card, auto_recharge, today:{spend_cents,cap_cents}, ledger[]}
      const r = await rpc<Json>('hh_mkt_billing_summary', { p_account: accountId });
      const b = r.balances ?? {};
      return {
        account_id: r.account_id,
        paid_cents: num(b.paid_cents),
        promo_cents: num(b.promo_cents),
        promo_next_expiry: b.promo_next_expiry ?? null,
        grants: arr<Json>(r.grants).map((g) => ({
          id: g.id, program_id: null, program_name: g.program ?? null, account_id: r.account_id, amount_cents: num(g.amount_cents),
          remaining_cents: num(g.remaining_cents), applies_to: arr<T.SpendPurpose>(g.applies_to), expires_at: g.expires_at,
          granted_at: g.granted_at, granted_by: null, note: g.note ?? null,
        })),
        card: { brand: r.card?.brand ?? null, last4: r.card?.last4 ?? null, ok: Boolean(r.card?.ok) },
        auto_recharge: {
          enabled: Boolean(r.auto_recharge?.enabled), threshold_cents: num(r.auto_recharge?.threshold_cents),
          amount_cents: num(r.auto_recharge?.amount_cents),
        },
        today: { spent_cents: num(r.today?.spend_cents), cap_cents: num(r.today?.cap_cents) },
        ledger: arr<Json>(r.ledger).map((l) => ({
          id: l.id, account_id: r.account_id, occurred_at: l.occurred_at, entry_type: l.entry_type, funding: l.funding,
          amount_cents: num(l.amount_cents), purpose: l.purpose ?? null, grant_id: l.grant_id ?? null, campaign_id: l.campaign_id ?? null,
          listing_id: l.listing_id ?? null, stripe_ref: l.stripe_ref ?? null, memo: l.memo ?? null,
        })),
      };
    },
    async ledger(accountId, from, to) {
      let q = sb().from('mkt_ledger').select('*').eq('account_id', accountId).order('occurred_at', { ascending: false }).limit(5000);
      if (from) q = q.gte('occurred_at', from);
      if (to) q = q.lte('occurred_at', `${to}T23:59:59Z`);
      return rows<T.LedgerRow>(q);
    },
    async redeemCode(accountId, code) {
      const r = await rpc<Json>('hh_mkt_redeem_code', { p_account: accountId, p_code: code });
      if (!r.ok) return { ok: false, message: REDEEM_REASON[r.reason as string] ?? 'That code couldn’t be redeemed.' };
      return { ok: true, grant_id: r.grant_id, amount_cents: num(r.amount_cents), expires_at: r.expires_at };
    },
    // mkt-stripe: {action, account, payment_method?, amount_cents?} (backend/edge-functions/mkt-stripe/index.ts)
    setupCard: (accountId) => edge<{ client_secret: string }>('mkt-stripe', { action: 'setup_card', account: accountId }),
    async confirmMockCard() {
      /* real mode: the Stripe webhook records the card */
    },
    async listCards(accountId) {
      const r = await edge<{ cards: T.Card[] }>('mkt-stripe', { action: 'list_cards', account: accountId });
      return arr<T.Card>(r?.cards);
    },
    async setDefaultCard(accountId, pm) {
      await edge('mkt-stripe', { action: 'set_default_card', account: accountId, payment_method: pm });
    },
    topup: (accountId, amount) => edge<{ client_secret: string }>('mkt-stripe', { action: 'topup', account: accountId, amount_cents: amount }),
    async confirmMockTopup() {
      /* real mode: the Stripe webhook credits the ledger */
    },
    identityStart: (accountId) => edge<{ url: string }>('mkt-stripe', { action: 'identity_start', account: accountId }),
    portalLink: (accountId) => edge<{ url: string }>('mkt-stripe', { action: 'portal_link', account: accountId }),

    // ── Settings ──
    apiKeys: (accountId) =>
      rows<T.ApiKey>(sb().from('mkt_api_keys').select('id, account_id, name, last4, created_at, revoked_at').eq('account_id', accountId).order('created_at', { ascending: false })),
    createApiKey: (accountId, name) => rpc<T.NewApiKey>('hh_mkt_create_api_key', { p_account: accountId, p_name: name }),
    revokeApiKey: (id) => rpc<void>('hh_mkt_revoke_api_key', { p_id: id }),
    webhooks: (accountId) =>
      rows<T.Webhook>(sb().from('mkt_webhooks').select('id, account_id, url, events, active, created_at').eq('account_id', accountId).order('created_at', { ascending: false })),
    createWebhook: (accountId, url, events) =>
      rpc<T.NewWebhook>('hh_mkt_create_webhook', { p_account: accountId, p_url: url, p_events: events }),
    deleteWebhook: (id) => rpc<void>('hh_mkt_delete_webhook', { p_id: id }),

    // ── Staff ──
    isStaff: () => rpc<boolean>('hh_mkt_is_staff').then(Boolean).catch(() => false),
    async adminOverview(from, to) {
      const r = await rpc<Json>('hh_mkt_admin_overview', { p_from: from, p_to: to });
      return {
        from, to,
        revenue: { paid_cents: num(r.revenue?.paid_spend_cents), promo_cents: num(r.revenue?.promo_spend_cents) },
        active_listings: num(r.active_listings),
        accounts: {
          total: num(r.accounts?.total), dealers: num(r.accounts?.dealers), individuals: num(r.accounts?.individuals),
          suspended: num(r.accounts?.suspended),
        },
        leads: num(r.leads),
        top_campaigns: arr<Json>(r.top_campaigns).map((c) => ({
          campaign_id: c.id, name: c.name, account_name: c.account_name ?? '', spend_cents: num(c.spend_cents),
          clicks: num(c.clicks), leads: num(c.leads),
        })),
        daily: arr<Json>(r.daily).map((d) => ({
          day: String(d.day).slice(0, 10), paid_cents: num(d.paid_cents), promo_cents: num(d.promo_cents), leads: num(d.leads),
        })),
      };
    },
    adminPolicies: () => rows<T.PricingPolicy>(sb().from('mkt_pricing_policies').select('*').order('effective_at', { ascending: false })),
    adminSetPolicy: (policy) => rpc<string>('hh_mkt_admin_set_policy', { p_policy: policy }),
    adminPrograms: () => rows<T.CreditProgram>(sb().from('mkt_credit_programs').select('*').order('created_at', { ascending: false })),
    adminCreateProgram: (p) => rpc<string>('hh_mkt_admin_create_program', { p_program: p }),
    async adminGrants() {
      const r = await rows<T.CreditGrant & { mkt_credit_programs?: { name: string } | null; mkt_accounts?: { display_name: string } | null }>(
        sb().from('mkt_credit_grants').select('*, mkt_credit_programs(name), mkt_accounts(display_name)').order('granted_at', { ascending: false }).limit(500),
      );
      return r.map(({ mkt_credit_programs: p, mkt_accounts: a, ...g }) => ({ ...g, program_name: p?.name ?? null, account_name: a?.display_name ?? null }));
    },
    adminGrantCredit: (i) =>
      rpc<string>('hh_mkt_admin_grant_credit', {
        p_account: i.account_id, p_amount_cents: i.amount_cents, p_expires_days: i.expires_days,
        p_applies_to: i.applies_to, p_note: i.note, p_program: i.program_id ?? null,
      }),
    async adminAccounts(q) {
      // hh_mkt_admin_accounts: account row + paid/promo balances, live listings, lifetime top-ups.
      const r = await rpc<Json[]>('hh_mkt_admin_accounts', { p_q: q || null });
      return (r ?? []).map((a) => ({
        ...(a as T.Account), storefront: (a.storefront ?? {}) as T.Storefront,
        active_listings: num(a.active_listings), paid_cents: num(a.paid_cents), promo_cents: num(a.promo_cents),
        lifetime_paid_cents: num(a.lifetime_paid_cents),
      }));
    },
    adminSetAccount: (accountId, patch) => rpc<void>('hh_mkt_admin_set_account', { p_account: accountId, p_patch: patch }),
    async adminReviewRuns(filter) {
      let q = sb().from('mkt_review_runs').select('*').order('occurred_at', { ascending: false }).limit(300);
      if (filter?.decision) q = q.eq('decision', filter.decision);
      if (filter?.target_type) q = q.eq('target_type', filter.target_type);
      return rows<T.ReviewRun>(q);
    },
    async adminOverrideReview(targetType, id, decision, reason) {
      await rpc<Json>('hh_mkt_admin_override_review', { p_target_type: targetType, p_id: id, p_decision: decision, p_reason: reason });
    },
    async adminAuditLog(filter) {
      let q = sb().from('mkt_audit_log').select('*').order('occurred_at', { ascending: false }).limit(500);
      if (filter?.action) q = q.ilike('action', `%${filter.action}%`);
      return rows<T.AuditEntry>(q);
    },
  };
}
